import { getData } from "actions";
import { CardContainer, CardDashboard, Container } from "components";
import { API_URL_ticketDashboard } from "constants/api";
import { AuthContext } from "context/AuthContext";
import useColorMode from "hooks/useColorMode";
import { useWindowSize } from "hooks/useWindowSize";
import moment from "moment";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  BsCalendarXFill,
  BsPersonXFill,
  BsTicketPerforatedFill,
} from "react-icons/bs";
import { FaCalendarDay, FaEnvelope } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SyncLoader } from "react-spinners";
import Datepicker from "react-tailwindcss-datepicker";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ticketDashboardReducers } from "redux/ticketDashboardSlice";

const Dashboard = () => {
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [width] = useWindowSize();
  const [colorMode, setColorMode] = useColorMode();
  const { countTicketInbox } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    setData([
      {
        title: "Inbox",
        name: "inbox",
        value: countTicketInbox && countTicketInbox.ticketinbox["inbox"],
        icons: <FaEnvelope />,
        color: "#0891b2",
      },
      {
        title: "My Tickets",
        name: "mytickets",
        value: countTicketInbox && countTicketInbox.ticketinbox["mytickets"],
        icons: <BsTicketPerforatedFill />,
        color: "#0d9488",
      },
      {
        title: "Unnasigned",
        name: "unassigned",
        value: countTicketInbox && countTicketInbox.ticketinbox["unassigned"],
        icons: <BsPersonXFill />,
        color: "#eab308",
      },
      {
        title: "Overdue",
        name: "overdue",
        value: countTicketInbox && countTicketInbox.ticketinbox["overdue"],
        icons: <BsCalendarXFill />,
        color: "#fb923c",
      },

      {
        title: "Due Today",
        name: "inbox",
        value: countTicketInbox && countTicketInbox.ticketinbox["duetoday"],
        icons: <FaCalendarDay />,
        color: "#ef4444",
        state: "duetoday",
      },
    ]);
  }, [countTicketInbox]);

  const { getTicketDashboardResult, getTicketDashboardLoading } = useSelector(
    (state) => state.ticketDashboard
  );

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_ticketDashboard,
        params,
        { dispatch, redux: ticketDashboardReducers },
        "GET_TICKETDASHBOARD"
      );
    },
    [dispatch]
  );

  const now = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(now.getMonth() - 1);
  const [value, setValue] = useState({
    startDate: oneMonthAgo,
    endDate: now,
  });

  const handleValueChange = (newValue) => {
    setValue(newValue);
    console.log(newValue);
  };

  useEffect(() => {
    if (value.startDate !== null && value.endDate !== null) {
      const startDate = moment(value.startDate).format("YYYY-MM-DD");
      const endDate = moment(value.endDate).format("YYYY-MM-DD");
      const param = `?startDate=${startDate}&endDate=${endDate}`;
      get(param);
    }
  }, [value]);

  useEffect(() => {
    if (datauser !== "") {
      //check permissionViewMenuAgent
      const permissionViewMenuAgent = datauser.group_permissions.includes(
        "access_menu_agent"
      );
      if (permissionViewMenuAgent) {
        setAdminAccess(true);
      } else {
        navigate("/");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {/* Card Dashboard */}
        {data &&
          data.map((item, index) => <CardDashboard key={index} {...item} />)}
      </div>

      {/* Graph */}
      <div className="mt-4">
        <CardContainer title="Report">
          <div className="p-4">
            {/* loading */}
            {getTicketDashboardLoading && (
              <div>
                <div className="pt-10 pb-6 flex justify-center items-center">
                  <SyncLoader color="#4477CE" />
                </div>
              </div>
            )}
            {/* result  */}
            {getTicketDashboardResult.data && (
              <>
                <div className="mb-4 flex justify-between gap-4 flex-wrap">
                  {/* Legend */}
                  <div className="flex gap-x-6 flex-wrap text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm shadow bg-[#7dd3fc]"></div>
                      <div>
                        {getTicketDashboardResult.data &&
                          getTicketDashboardResult.data?.ticket_created}{" "}
                        Ticket Created
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm shadow bg-[#34d399]"></div>
                      <div>
                        {getTicketDashboardResult.data &&
                          getTicketDashboardResult.data?.ticket_reopened}{" "}
                        Ticket Reopened
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm shadow bg-[#f87171]"></div>
                      <div>
                        {getTicketDashboardResult.data &&
                          getTicketDashboardResult.data?.ticket_closed}{" "}
                        Ticket Closed
                      </div>
                    </div>
                  </div>

                  <div className="w-full sm:w-60 ">
                    <Datepicker
                      popoverDirection="down"
                      inputClassName={
                        "w-full hidden:button border rounded list-none outline-none px-4 py-2 text-sm border-neutral-300 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral"
                      }
                      useRange={false}
                      showShortcuts={width < 1024 ? false : true}
                      displayFormat="DD-MM-YYYY"
                      value={value}
                      onChange={handleValueChange}
                    />
                  </div>
                </div>
                <div>
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart
                      data={
                        getTicketDashboardResult.data &&
                        getTicketDashboardResult.data?.daily
                      }
                    >
                      <CartesianGrid strokeDasharray="1 0" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip
                        contentStyle={{
                          backgroundColor:
                            colorMode === "dark" ? "#111827" : "white",
                          border: 0,
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="created"
                        stroke="#7dd3fc"
                        fill="url(#createdID)"
                      />
                      <Area
                        type="monotone"
                        dataKey="reopened"
                        stroke="#34d399"
                        fill="url(#reopenedID)"
                      />
                      <Area
                        type="monotone"
                        dataKey="closed"
                        stroke="#f87171"
                        fill="url(#closedID)"
                      />
                      <defs>
                        <linearGradient
                          id="createdID"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#7dd3fc"
                            stopOpacity={0.6}
                          />
                          <stop
                            offset="95%"
                            stopColor="#7dd3fc"
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient
                          id="reopenedID"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#34d399"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor="#34d399"
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient
                          id="closedID"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#f87171"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor="#f87171"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>
        </CardContainer>
      </div>

      <div className="mt-4">
        <CardContainer title="Statistics">
          <div className="p-4">
            {/* loading */}
            {getTicketDashboardLoading && (
              <div>
                <div className="pt-10 pb-6 flex justify-center items-center">
                  <SyncLoader color="#4477CE" />
                </div>
              </div>
            )}
            {getTicketDashboardResult.data && (
              <>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-300 dark:border-neutral-700">
                      <th className="text-left p-2">Departments</th>
                      <th className="text-left p-2">Closed</th>
                      <th className="text-left p-2">Deleted</th>
                      <th className="text-left p-2">Open</th>
                      <th className="text-left p-2">Resolved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getTicketDashboardResult.data &&
                      getTicketDashboardResult.data?.ticket_departement.map(
                        (item, index) => (
                          <tr
                            key={index}
                            className="border-b border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-700 text-sm"
                          >
                            <td className="text-left p-2">{item.name}</td>
                            <td className="text-left p-2">{item.closed}</td>
                            <td className="text-left p-2">{item.deleted}</td>
                            <td className="text-left p-2">{item.open}</td>
                            <td className="text-left p-2">{item.resolved}</td>
                          </tr>
                        )
                      )}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </CardContainer>
      </div>
    </Container>
  );
};

export default Dashboard;
